import React, { useState, useEffect } from "react";
import { useLocation } from 'react-router-dom';
import { useGet } from "../../customHooks/useGet";
import { useAuthProvider } from "../../customHooks/authContext";
import { Container, Table, Alert, Button } from "react-bootstrap";
import { FaPlus, FaArrowLeft, FaArrowRight, FaEdit, FaTrash, FaArrowUp, FaArrowDown } from "react-icons/fa"; 
import PerformanceForm from "./PerformanceForm";
import PerformanceEdit from "./PerformanceEdit";
import { del, put } from "../../services/Services"; 
import "bootstrap/dist/css/bootstrap.min.css";
import ConfirmModal from "./ConfirmModal";


const FloatingButton = ({ onClick, icon, title, position }) => (
  <button
    className="btn position-fixed rounded-circle p-3 shadow-lg d-flex align-items-center justify-content-center"
    onClick={onClick}
    aria-label={title}
    title={title}
    style={{
      background: "linear-gradient(135deg, #007bff, #6610f2)",
      color: "white",
      fontSize: "1.5rem",
      border: "none",
      width: "60px",
      height: "60px",
      transition: "transform 0.2s ease, box-shadow 0.3s ease",
      zIndex: 1050,
      bottom: "20px",
      right: position === "add" ? "20px" : "90px",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "scale(1.1)";
      e.currentTarget.style.boxShadow = "0px 0px 15px rgba(102, 16, 242, 0.6)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "scale(1)";
      e.currentTarget.style.boxShadow = "0px 4px 10px rgba(0, 0, 0, 0.2)";
    }}
  >
    {icon}
  </button>
);

const Performance = () => {
  const location = useLocation();
  const { eventRegistrationId, eventId } = location.state || {};
  const { token } = useAuthProvider();
  const [page, setPage] = useState(0);
  const size = 8; // 📌 Tamaño de la página
  const [showForm, setShowForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedPerformance, setSelectedPerformance] = useState(null);
  const [showHelpMessage, setShowHelpMessage] = useState(true); // Estado para el mensaje de ayuda
  const { data: performancesData, refetch } = useGet(
    eventRegistrationId
      ? `https://ectech-software.com.ar/api/academy/events/registration/${eventRegistrationId}/performance?page=${page}&size=${size}`
      : null,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const totalPages = performancesData?.totalPages || 1;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPerformanceId, setSelectedPerformanceId] = useState(null);
  const handleDelete = () => {
    console.log("Obra eliminada"); // Aquí va la lógica de eliminación
    setIsModalOpen(false);
  };

  // 🔹 Ocultar el mensaje de ayuda después de 30 segundos
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowHelpMessage(false);
    }, 10000); // 30 segundos

    return () => clearTimeout(timer);
  }, []);

  const handleNewPerformance = () => {
    setSelectedPerformance(null);
    setShowForm(true);
    setShowEditForm(false);
  };

  const handleEditPerformance = (performance) => {
    setSelectedPerformance(performance);
    setShowEditForm(true);
    setShowForm(false);
  };

  const handleDeletePerformance = async (id) => {
    try {
      const response = await del(
        `/api/academy/events/registration/${eventRegistrationId}/performance/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
  
      if (response.status === 200) {
        refetch();
        setSelectedPerformanceId(null); // Cerrar el modal después de eliminar
      } else {
        alert("No se pudo eliminar la obra.");
      }
    } catch (error) {
      console.error("Error al eliminar:", error);
      alert("Hubo un problema al eliminar la obra.");
    }
  };
  

  const handleFormClose = (reload = false) => {
    setShowForm(false);
    setShowEditForm(false);
    setSelectedPerformance(null);
    if (reload) refetch();
  };

 

  // 📌 Función para cambiar el orden de las obras
  const handleMoveUp = async (performance, index) => {
    if (index > 0) {
      const newOrder = performance.order - 1;
      await updatePerformanceOrder(performance.id, newOrder);
    }
  };

  const handleMoveDown = async (performance, index) => {
    if (index < performancesData.content.length) {
      const newOrder = performance.order + 1;
      await updatePerformanceOrder(performance.id, newOrder);
    }
  };

  const updatePerformanceOrder = async (performanceId, newOrder) => {
    try {
      const response = await put(
        `/api/academy/events/registration/${eventRegistrationId}/performance/${performanceId}/order`,
        { order: newOrder }, // 📌 Este es el body correcto
        { headers: { Authorization: `Bearer ${token}` } } // 📌 Configuración de headers
      );
  
      if (response.status === 200) {
        console.log("✅ Orden actualizada correctamente.");
        refetch(); // Refrescar la lista desde la API
      } else {
        console.error("❌ No se pudo cambiar el orden. Respuesta del servidor:", response);
        alert("No se pudo cambiar el orden.");
      }
    } catch (error) {
      console.error("❌ Error al cambiar el orden:", error);
      alert("Hubo un problema al cambiar el orden.");
    }
  };
  
  

  return (
    <Container className="container mt-4 pb-5">
       <h1 className="text-center">Mis Obras</h1>
      {/* Mostrar mensaje de ayuda si showHelpMessage es true */}
      {showHelpMessage && (
        <Alert variant="danger" className="text-center">
          Ayuda: Aquí puedes gestionar todas tus obras, generar nuevas, modificarlas y además eliminarlas.
        </Alert>
      )}
     

      {performancesData?.content?.length === 0 ? (
        <Alert variant="info" className="text-center">
          No hay Obras disponibles.
        </Alert>
        ) : (
        <>
          <Table striped bordered hover className="text-center">
            <thead className="table-primary">
              <tr>
                <th>Orden</th>
                <th>Nombre</th>
                <th>Categoría</th>
                <th>Modalidad</th>
                <th>Forma</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
         {performancesData?.content
       ?.slice() // Clonamos el array para no mutar el original
      .sort((a, b) => a.order - b.order) // Orden ascendente por order
      .map((performance, index) => (
      <tr key={`${performance.id}-${index}`}>
        <td className="fw-bold">{performance.order}</td>
        <td>{performance.name}</td>
        <td>{performance.category?.name || "N/A"}</td>
        <td>{performance.modality?.name || "N/A"}</td>
        <td>{performance.form?.name || "N/A"}</td>
        <td>
          <button
            className="btn btn-warning btn-sm me-2"
            onClick={() => handleEditPerformance(performance)}
          >
            <FaEdit /> Editar
          </button>
          <button
                className="btn btn-danger btn-sm me-2"
                onClick={() => setSelectedPerformanceId(performance.id)}
              >
                <FaTrash /> Eliminar
              </button>

              <ConfirmModal
                isOpen={selectedPerformanceId === performance.id}
                onClose={() => setSelectedPerformanceId(null)}
                onConfirm={() => handleDeletePerformance(selectedPerformanceId)}
              />

         { /*<button
            className="btn btn-danger btn-sm me-2"
            onClick={() => handleDeletePerformance(performance.id)}
          >
            <FaTrash /> Eliminar
          </button>*/}
          <button
            className="btn btn-success btn-sm me-2"
            onClick={() => handleMoveUp(performance, index)}
          >
            <FaArrowUp /> Subir
          </button>
          <button
            className="btn btn-primary btn-sm me-2"
            onClick={() => handleMoveDown(performance,index)}
          >
            <FaArrowDown /> Bajar
          </button>
        </td>
      </tr>
      ))}
     </tbody>

          </Table>

          {/* 📌 Controles de paginación */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <Button variant="secondary" onClick={() => setPage(page - 1)} disabled={page === 0}>
              <FaArrowLeft /> Anterior
            </Button>
            <span className="fw-bold">Página {page + 1} de {totalPages}</span>
            <Button variant="secondary" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
              Siguiente <FaArrowRight />
            </Button>
          </div>
        </>
      )}

      {/* Botón flotante */}
      <FloatingButton
        onClick={handleNewPerformance}
        icon={<FaPlus size={24} />}
        title="Agregar nueva obra"
        position="add"
      />

      <div className="col-md-6 col-lg-8 mx-auto">
        {showForm && <PerformanceForm onclose={handleFormClose} onSuccess={refetch} eventId={eventId} eventRegistrationId={eventRegistrationId} />}
        {showEditForm && (
          <PerformanceEdit
            onclose={handleFormClose}
            onSuccess={refetch}
            performanceId={selectedPerformance.id}
            eventId={eventId}
            eventRegistrationId={eventRegistrationId}
          />
        )}
      </div>
    </Container>
  );
};

export default Performance;
